import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, type Variants } from 'framer-motion'
import { Lock, User, Envelope, ArrowRight, Eye, EyeSlash, WarningCircle, Check } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { DotLoader } from '@/components/ui/DotLoader'
import { teamApi, type InviteValidation } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import {
  authInputClass as inputClass,
  authInputStyle as inputStyle,
  errorBorderClass,
} from '@/lib/styleKit'

// This page is the frontend half of the invite flow. The backend
// (POST /team/invites, team_service.create_or_replace_invite) has been emailing
// links to `${FRONTEND_URL}/team/accept-invite?token=...` since the Team
// settings feature shipped, and the validate/accept endpoints + teamApi client
// functions have existed just as long. No route or page ever consumed them,
// so every invite link 404'd. This closes that gap; nothing on the backend
// changed.

const acceptSchema = z
  .object({
    full_name: z
      .string()
      .trim()
      .min(2, 'Full name must be at least 2 characters')
      .regex(
        /^[\p{L}][\p{L}\s'.-]*$/u,
        'Name can only contain letters, spaces, hyphens, apostrophes, and periods',
      ),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Use upper, lower, and a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type AcceptFormData = z.infer<typeof acceptSchema>

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  representative: 'Representative',
  rep: 'Representative', // legacy invites created before the value rename
  analyst: 'Analyst',
}

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: { x: [0, -6, 6, -4, 4, 0], transition: { duration: 0.4, ease: 'easeInOut' } },
}

/** Terminal state for a link that can never work: missing token, or one the
 * backend rejected as not found / revoked / accepted / expired. Same shape as
 * ResetPasswordPage's DeadLinkScreen: there is no retry, only "get a fresh one". */
const DeadLinkScreen: React.FC<{ message: string }> = ({ message }) => {
  const navigate = useNavigate()
  return (
    <motion.div className="w-full" variants={formStageVariants} initial="hidden" animate="show">
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Invalid <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">invite</span>
        </h1>
      </motion.div>
      <motion.div variants={formItemVariants} className="space-y-6">
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/[0.06] p-4"
        >
          <WarningCircle
            className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
            weight="fill"
          />
          <p className="text-[#7a5d2a] dark:text-[#e0c089]" style={{ fontSize: '13.5px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
            {message}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full h-10 rounded-md bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0] text-white inline-flex items-center justify-center gap-2 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          Go to sign in
        </button>
        <p className="text-center text-[#5f6166] dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
          Ask whoever invited you to send a new link.
        </p>
      </motion.div>
    </motion.div>
  )
}

const AcceptInvitePage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const currentUser = useAuthStore((s) => s.user)
  const loginWithTokens = useAuthStore((s) => s.loginWithTokens)

  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirm, setShowConfirm] = React.useState(false)
  const [isValidating, setIsValidating] = React.useState(true)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [invite, setInvite] = React.useState<InviteValidation | null>(null)
  // Set once, from either a missing token or a rejected one. A dead invite
  // fails the same way on every retry, so we leave the form entirely.
  const [deadLinkMessage, setDeadLinkMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!token) {
      setDeadLinkMessage('This invite link is missing or incomplete. Ask whoever invited you to resend it.')
      setIsValidating(false)
      return
    }
    let cancelled = false
    teamApi
      .validateInvite(token)
      .then((response) => {
        if (!cancelled) setInvite(response.data)
      })
      .catch((error) => {
        if (cancelled) return
        // validate_invite answers an unknown/expired/already-used token with a
        // plain-text detail ("This invite has been revoked.", 404 "Invite not
        // found"), surface it as-is rather than a generic message.
        const detail = error?.response?.data?.detail
        setDeadLinkMessage(
          typeof detail === 'string' && detail
            ? detail
            : 'This invite link is invalid. Ask whoever invited you to resend it.',
        )
      })
      .finally(() => {
        if (!cancelled) setIsValidating(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptFormData>({ resolver: zodResolver(acceptSchema) })

  const passwordValue = watch('password') || ''

  // Shared by both accept paths (existing-account "just confirm" and
  // fresh-signup "set a password"): the only difference is what payload
  // each one sends; the backend, the token-went-stale handling, and the
  // post-accept login/redirect are identical either way.
  const completeAccept = async (payload: { full_name?: string; password?: string }) => {
    setIsSubmitting(true)
    try {
      const response = await teamApi.acceptInvite(token, payload)
      const { access_token, refresh_token } = response.data
      await loginWithTokens(access_token, refresh_token)
      toast.success(`Welcome to ${invite?.account_name ?? 'the team'}!`)
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      // A token that was valid moments ago but got revoked/expired/already-
      // accepted in the gap between validating and submitting, same terminal
      // treatment as failing validation outright.
      if (error?.response?.status === 410 || error?.response?.status === 404) {
        setDeadLinkMessage(
          typeof detail === 'string' && detail ? detail : 'This invite is no longer valid.',
        )
        return
      }
      toast.error(detail || 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmit = (data: AcceptFormData) => completeAccept({ full_name: data.full_name, password: data.password })
  const onAcceptExisting = () => completeAccept({})

  if (isValidating) {
    return (
      <div className="w-full flex flex-col items-center justify-center gap-3 py-16">
        <DotLoader size="md" announce />
        <p className="text-[#5f6166] dark:text-[#8a8f98]" style={{ fontSize: '13.5px', letterSpacing: '-0.13px' }}>
          Checking your invite&hellip;
        </p>
      </div>
    )
  }

  if (deadLinkMessage || !invite) {
    return <DeadLinkScreen message={deadLinkMessage ?? 'This invite link is invalid.'} />
  }

  const switchingAccounts = !!currentUser && currentUser.email.toLowerCase() !== invite.email.toLowerCase()

  return (
    <motion.div className="w-full" variants={formStageVariants} initial="hidden" animate="show">
      {/* Header */}
      <motion.div className="mb-6" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Join <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">{invite.account_name}</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{ fontSize: '15px', lineHeight: 1.5, letterSpacing: '-0.165px', fontWeight: 400 }}
        >
          You've been invited as{' '}
          <span className="text-[#0f1011] dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
            {ROLE_LABELS[invite.role] ?? invite.role}
          </span>
        </p>
      </motion.div>

      {/* Invited email: fixed by the invite, not editable here */}
      <motion.div
        className="mb-6 flex items-center gap-2.5 rounded-md border border-black/[0.06] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] px-3.5 py-2.5"
        variants={formItemVariants}
      >
        <Envelope className="h-4 w-4 text-[#7a7d83] dark:text-[#62666d] shrink-0" weight="regular" />
        <span className="text-[#0f1011] dark:text-[#f7f8f8] truncate" style={{ fontSize: '13.5px', letterSpacing: '-0.13px' }}>
          {invite.email}
        </span>
      </motion.div>

      {switchingAccounts && (
        <motion.div
          role="status"
          className="mb-6 flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/[0.06] p-3.5"
          variants={formItemVariants}
        >
          <WarningCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" weight="fill" />
          <p className="text-[#7a5d2a] dark:text-[#e0c089]" style={{ fontSize: '12.5px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
            You're currently signed in as {currentUser?.email}. Continuing will switch you to {invite.email}.
          </p>
        </motion.div>
      )}

      {invite.existing_account ? (
        // This email already has a Beevelope account. accept_invite()
        // re-parents (or merges) it onto this account and never touches a
        // password, so there's nothing to fill in. A password field here
        // would be actively misleading: it would look load-bearing and
        // silently do nothing.
        <motion.div variants={formItemVariants} className="space-y-5">
          <div className="flex items-start gap-3 rounded-md border border-black/[0.06] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] p-3.5">
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" weight="bold" />
            <p className="text-[#5f6166] dark:text-[#8a8f98]" style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
              You already have a Beevelope account with this email. Accepting will move it to{' '}
              <span className="text-[#0f1011] dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>{invite.account_name}</span>.
              No new password needed.
            </p>
          </div>
          <motion.button
            type="button"
            onClick={onAcceptExisting}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
            style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
            whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
            whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
          >
            {isSubmitting ? (
              <>
                <DotLoader size="sm" announce={false} />
                <span>Joining</span>
              </>
            ) : (
              <motion.span className="inline-flex items-center gap-2" whileHover="hover">
                Accept invite
                <motion.span variants={{ hover: { x: 3 } }} transition={{ duration: 0.2, ease: easeOutExpo }} className="inline-flex">
                  <ArrowRight className="h-4 w-4" weight="bold" />
                </motion.span>
              </motion.span>
            )}
          </motion.button>
        </motion.div>
      ) : (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Full name */}
        <motion.div variants={formItemVariants}>
          <motion.div className="relative group" variants={shakeVariants} animate={errors.full_name ? 'error' : 'idle'}>
            <User
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="full_name"
              type="text"
              autoComplete="name"
              placeholder="Full name"
              aria-label="Full name"
              aria-invalid={errors.full_name ? true : undefined}
              aria-describedby={errors.full_name ? 'full_name-error' : undefined}
              autoFocus
              style={inputStyle}
              className={`${inputClass} ${errors.full_name ? errorBorderClass : ''}`}
              {...register('full_name')}
            />
          </motion.div>
          {errors.full_name && (
            <motion.p
              id="full_name-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.full_name.message}
            </motion.p>
          )}
        </motion.div>

        {/* Password */}
        <motion.div variants={formItemVariants}>
          <motion.div className="relative group" variants={shakeVariants} animate={errors.password ? 'error' : 'idle'}>
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Password"
              aria-label="Password"
              aria-invalid={errors.password ? true : undefined}
              aria-describedby={errors.password ? 'password-requirements password-error' : 'password-requirements'}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.password ? errorBorderClass : ''}`}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showPassword ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          <p id="password-requirements" className="sr-only">
            Password must be at least 8 characters and include a lowercase letter, an uppercase letter, and a number.
          </p>
          <div aria-hidden="true" className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
            {[
              { label: '8+ characters', test: (v: string) => v.length >= 8 },
              { label: 'Lowercase', test: (v: string) => /[a-z]/.test(v) },
              { label: 'Uppercase', test: (v: string) => /[A-Z]/.test(v) },
              { label: 'Number', test: (v: string) => /\d/.test(v) },
            ].map((rule) => {
              const met = rule.test(passwordValue)
              return (
                <span
                  key={rule.label}
                  className={`inline-flex items-center gap-1 transition-colors duration-150 ${
                    met ? 'text-emerald-700 dark:text-emerald-400' : 'text-[#5f6166] dark:text-[#8a8f98]'
                  }`}
                  style={{ fontSize: '12px', letterSpacing: '-0.1px', fontWeight: 400 }}
                >
                  <Check className={`h-3.5 w-3.5 transition-opacity duration-150 ${met ? 'opacity-100' : 'opacity-40'}`} weight="bold" />
                  {rule.label}
                </span>
              )
            })}
          </div>
          {errors.password && (
            <motion.p
              id="password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.password.message}
            </motion.p>
          )}
        </motion.div>

        {/* Confirm password */}
        <motion.div variants={formItemVariants}>
          <motion.div className="relative group" variants={shakeVariants} animate={errors.confirmPassword ? 'error' : 'idle'}>
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirm password"
              aria-label="Confirm password"
              aria-invalid={errors.confirmPassword ? true : undefined}
              aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.confirmPassword ? errorBorderClass : ''}`}
              {...register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              aria-pressed={showConfirm}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showConfirm ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          {errors.confirmPassword && (
            <motion.p
              id="confirm-password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.confirmPassword.message}
            </motion.p>
          )}
        </motion.div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
          variants={formItemVariants}
          whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
          whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
        >
          {isSubmitting ? (
            <>
              <DotLoader size="sm" announce={false} />
              <span>Joining</span>
            </>
          ) : (
            <motion.span className="inline-flex items-center gap-2" whileHover="hover">
              Accept invite
              <motion.span variants={{ hover: { x: 3 } }} transition={{ duration: 0.2, ease: easeOutExpo }} className="inline-flex">
                <ArrowRight className="h-4 w-4" weight="bold" />
              </motion.span>
            </motion.span>
          )}
        </motion.button>
      </form>
      )}
    </motion.div>
  )
}

export default AcceptInvitePage
